import React from "react";
import { Autocomplete, TextField } from "@mui/material";

export type DriveFolderNameFieldProps = {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  disabled?: boolean;
  readOnly?: boolean;
  compact?: boolean;
  label?: string;
  placeholder?: string;
};

/**
 * Single folder field: pick an existing folder from the list or type a new name (freeSolo).
 */
const DriveFolderNameField: React.FC<DriveFolderNameFieldProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  readOnly = false,
  compact = false,
  label = "Drive folder",
  placeholder = "Choose or type a folder name",
}) => {
  const fieldSx = {
    mb: compact ? 0 : 2.4,
    "& .MuiOutlinedInput-root": {
      borderRadius: (t: { shape: { borderRadius: number } }) =>
        `${t.shape.borderRadius}px`,
      minHeight: 40,
    },
  };
  if (readOnly) {
    return (
      <TextField
        fullWidth
        size="small"
        label="Folder"
        value={value}
        disabled
        sx={fieldSx}
      />
    );
  }

  return (
    <Autocomplete
      freeSolo
      fullWidth
      disabled={disabled}
      options={options}
      value={value}
      inputValue={value}
      onInputChange={(_, nextValue, reason) => {
        if (reason === "reset") return;
        onChange(nextValue);
      }}
      onChange={(_, nextValue) => {
        onChange(typeof nextValue === "string" ? nextValue : nextValue || "");
      }}
      onBlur={(e) => {
        const input = (e.target as HTMLInputElement).value?.trim();
        if (input && input !== value) {
          onChange(input);
        }
      }}
      noOptionsText="No folders in this circle yet — type a name to create one"
      sx={fieldSx}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          name="driveFolderKey"
          autoComplete="off"
          {...(label ? { label } : {})}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default DriveFolderNameField;
